// Register / edit Experimental Configuration drawer.
//
// One component, two modes — `state.mode === "new"` vs `state.mode === "edit"`.
// Editing toggles auto-save for the alias / notes / alkane ladder / tags and
// gates the methodology behind an explicit Save & overwrite (which forces
// re-analysis of attached files). Registering keeps a simpler Cancel / Save
// footer because nothing is attached yet.
//
// Visual language mirrors the inventory sidebar:
//   - Top chrome — trash · slash label · save pill · close.
//   - Title block — huge alias input.
//   - Identity band — vendor, model, stationary phase, ionization energy
//     (the four required fields surfaced front-of-house).
//   - Alkane ladder + Tags + Notes (always visible, auto-save).
//   - Advanced methodology fold (column dimensions, MS detail, inlet, oven,
//     free-text method).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Icon } from "@/components/Icon/Icon";
import {
  abandonDraftLadders,
  archiveConfig,
  attachTag,
  createConfig,
  createTag,
  detachTag,
  listConfigLadders,
  patchConfig,
  type LadderRow,
  type TagDTO,
} from "@/services/instruments_api";
import { profileToCreateInput, profileToUpdateInput } from "@/lib/instrumentsDto";
import { instrumentsKeys } from "@/hooks/useInstruments";
import {
  AlkaneLadderEditor,
  type DraftLadderUpload,
} from "./AlkaneLadderEditor";
import {
  INSTRUMENT_VENDORS,
  findAnalyser,
  useInstrumentsCatalogs,
} from "@/hooks/useInstruments";
import type {
  AlkaneLadder,
  CarrierGas,
  InjectionMode,
  InstrumentProfile,
  InstrumentVendorId,
  Ionization,
  OvenStep,
} from "@/utils/types/instrument";
import { formatOven } from "./InstrumentsPage";

export type InstrumentDrawerState =
  | { mode: "new" }
  | { mode: "edit"; profile: InstrumentProfile };

const CARRIERS: CarrierGas[] = ["Helium", "Hydrogen", "Nitrogen"];
const INJECTION_MODES: InjectionMode[] = [
  "Splitless",
  "Split 1:5",
  "Split 1:10",
  "Split 1:20",
  "Split 1:50",
  "PTV",
];
const IONIZATIONS: Ionization[] = ["EI", "CI", "ESI", "APCI"];
const SCAN_MODES = ["Full scan", "SIM", "MRM", "Data-dependent MS²", "Full scan · 25k res"];

// Empty profile for the Register drawer. Numeric Advanced fields start
// as null so the placeholders show through and `profileToCreateInput`
// omits them from the methodology jsonb unless the user types a value.
// Picker enums (carrier / inlet mode / ionization / scan mode) keep
// their first-pill selection — those aren't free-form inputs.
// Empty profile for the Register drawer — every Advanced methodology
// field starts unset so the inputs show their placeholder hints and the
// picker rows ("Carrier gas", "Injection mode", "Ionization technique",
// "Scan mode") read "Choose…" until the user picks.
const emptyProfile = (): InstrumentProfile => ({
  id: "",
  alias: "",
  instrumentId: null,
  column: { id: null, length_m: null, id_mm: null, film_um: null },
  inlet: {
    carrier: null,
    flow_ml_min: null,
    temp_C: null,
    mode: null,
    vol_uL: null,
  },
  oven: [],
  ms: {
    ionization: null,
    sourceTemp_C: null,
    energy_eV: null,
    scanLo: null,
    scanHi: null,
    mode: null,
  },
  methodology: { notes: "" },
  alkaneLadder: [],
  tags: [],
  notes: "",
  createdAt: "",
  lastUsedAt: null,
  chromatograms: [],
});

type InstrumentDrawerProps = {
  state: InstrumentDrawerState;
  tagPool: string[];
  tagPoolMap: Map<string, TagDTO>;
  getTagUsage: (tag: string) => number;
  existingAliases: string[];
  onCreateTag?: (tag: string) => void;
  onRenameTag?: (oldT: string, newT: string) => void;
  onDeleteTag?: (tag: string) => void;
  onClose: () => void;
  onSave: (draft: InstrumentProfile, opts?: { asNew?: boolean }) => void;
  onDelete?: () => void;
};

export function InstrumentDrawer({
  state,
  tagPool,
  tagPoolMap,
  getTagUsage: _getTagUsage,
  existingAliases,
  onCreateTag,
  onRenameTag: _onRenameTag,
  onDeleteTag: _onDeleteTag,
  onClose,
  onSave,
  onDelete,
}: InstrumentDrawerProps) {
  const isEdit = state.mode === "edit";

  // New-mode draft scope for alkane ladders. Generated once on mount;
  // every ladder uploaded before Register tags its file_uploads.draft_id
  // with this value. On Register we hand the file_upload_ids to the
  // server so Stage 3b of registerOrEdit claims them onto the new
  // user_exp_config. On drawer close-without-register, we fire bulk
  // abandon so the rows don't linger as orphans.
  const [draftId] = useState(() => (isEdit ? "" : crypto.randomUUID()));
  const [draftLadders, setDraftLadders] = useState<DraftLadderUpload[]>([]);

  // Migrate older fixtures (no methodology.notes, no alkaneLadder array, no
  // tags) into the shape this drawer expects so the form has stable defaults.
  const initial = useMemo<InstrumentProfile>(() => {
    if (state.mode !== "edit") return emptyProfile();
    const base: InstrumentProfile = JSON.parse(JSON.stringify(state.profile));
    if (!base.methodology || typeof base.methodology.notes !== "string") {
      base.methodology = { notes: "" };
    }
    if (!Array.isArray(base.alkaneLadder)) {
      base.alkaneLadder = base.alkaneLadder ? [base.alkaneLadder as unknown as AlkaneLadder] : [];
    }
    if (!Array.isArray(base.tags)) base.tags = [];
    return base;
  }, [state]);

  const [d, setD] = useState<InstrumentProfile>(initial);
  const baselineRef = useRef<InstrumentProfile>(JSON.parse(JSON.stringify(initial)));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [autoSavedTick, setAutoSavedTick] = useState(0);

  const update = (patch: Partial<InstrumentProfile>) => setD((s) => ({ ...s, ...patch }));
  const updateInlet = (patch: Partial<InstrumentProfile["inlet"]>) =>
    setD((s) => ({ ...s, inlet: { ...s.inlet, ...patch } }));
  const updateMS = (patch: Partial<InstrumentProfile["ms"]>) =>
    setD((s) => ({ ...s, ms: { ...s.ms, ...patch } }));
  const updateCol = (patch: Partial<InstrumentProfile["column"]>) =>
    setD((s) => ({ ...s, column: { ...s.column, ...patch } }));
  const updateMeth = (patch: Partial<NonNullable<InstrumentProfile["methodology"]>>) =>
    setD((s) => ({
      ...s,
      methodology: { notes: s.methodology?.notes ?? "", ...patch },
    }));

  const { instrumentCatalog, columnFamilies, findInstrumentCatalog } =
    useInstrumentsCatalogs();
  const inst = findInstrumentCatalog(d.instrumentId);
  const [vendor, setVendor] = useState<InstrumentVendorId | null>(() => inst?.vendor ?? null);

  const instOptions = useMemo(
    () =>
      instrumentCatalog
        .filter((x) => !vendor || x.vendor === vendor)
        .map((x) => ({
          value: x.id,
          label: `${x.label}  ·  ${x.platform}`,
        })),
    [vendor, instrumentCatalog],
  );

  const colOptions = useMemo(
    () =>
      columnFamilies.map((c) => ({
        value: c.id,
        label: `${c.family}  —  ${c.polarity}`,
      })),
    [columnFamilies],
  );

  const nameTaken = useMemo(() => {
    const n = d.alias.trim().toLowerCase();
    if (!n) return false;
    return existingAliases.some((x) => (x || "").trim().toLowerCase() === n);
  }, [d.alias, existingAliases]);

  const canSave =
    !!(d.alias.trim() && d.instrumentId && d.column.id && d.ms.energy_eV != null) && !nameTaken;

  // F9: server-side alkane ladders for the open config. Empty in new mode;
  // populated in edit mode. The AlkaneLadderEditor (see far below) receives
  // this list as the canonical state and uses presignLadder / completeLadder
  // / archiveLadder for mutations. Declared up here (rather than next to the
  // editor) so `fileCount` below can read it without TDZ.
  const laddersQ = useQuery({
    queryKey:
      isEdit && state.profile?.id
        ? instrumentsKeys.configLadders(state.profile.id)
        : ["instruments", "ladders", "noop"],
    queryFn: () =>
      isEdit && state.profile?.id ? listConfigLadders(state.profile.id) : Promise.resolve([]),
    enabled: isEdit && !!state.profile?.id,
  });
  const serverLadders: LadderRow[] = laddersQ.data ?? [];

  // fileCount surfaces "N files will be re-analysed on overwrite" copy in
  // the methodology footer + delete-confirm modal. Use the server-canonical
  // ladders + (still-local) chromatograms collections; F11 wires server-side
  // chromatograms next.
  const fileCount = (d.chromatograms?.length || 0) + serverLadders.length;
  const hasFiles = fileCount > 0;

  // The protected ("methodology") keys are everything except alias, notes,
  // alkaneLadder, tags. Auto-save covers those four; everything else needs
  // an explicit Save & overwrite while editing.
  const stripAuto = (x: InstrumentProfile) => {
    const { alias: _a, notes: _n, alkaneLadder: _al, tags: _t, ...rest } = x;
    return rest;
  };
  const isProtectedDirty = useMemo(() => {
    return JSON.stringify(stripAuto(d)) !== JSON.stringify(stripAuto(baselineRef.current));
  }, [d]);
  // F4: autosave for name/notes only. Tag and ladder changes fire their
  // own direct mutations (F8 / F9) on user interaction, NOT via this
  // autosave path, so we don't track them in autoSaveDirty.
  const autoSaveDirty = useMemo(() => {
    const b = baselineRef.current;
    return (
      (d.alias || "") !== (b.alias || "") ||
      (d.notes || "") !== (b.notes || "")
    );
  }, [d.alias, d.notes]);

  const requiresManualSave = isEdit && isProtectedDirty;

  // ── Create-mode mutation (F3) + autosave name/notes mutation (F4).
  // Methodology save still routes through props.onSave; F5 swaps that. ──
  const qc = useQueryClient();
  const createM = useMutation({
    mutationFn: (input: ReturnType<typeof profileToCreateInput>) =>
      createConfig(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: instrumentsKeys.configs });
      onClose();
    },
  });

  // F4: autosave name/notes via PATCH. Fires on a 400ms debounce. On 409
  // (name collision) the alias is reverted to the last-known-good baseline
  // and the existing nameTaken inline warning surfaces.
  const patchM = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { name?: string; notes?: string | null };
    }) => patchConfig(id, patch),
    onSuccess: (_data, variables) => {
      baselineRef.current = {
        ...baselineRef.current,
        ...("name" in variables.patch ? { alias: variables.patch.name ?? "" } : {}),
        ...("notes" in variables.patch ? { notes: variables.patch.notes ?? "" } : {}),
      };
      setAutoSavedTick((n) => n + 1);
      void qc.invalidateQueries({ queryKey: instrumentsKeys.configs });
    },
    onError: (err) => {
      // 409 name_in_use → revert alias to baseline so the user sees the
      // last value the server accepted; the inline "name already exists"
      // warning then drives them to choose a different one.
      if (axios.isAxiosError(err)) {
        const body = err.response?.data as { error?: string } | undefined;
        if (body?.error === "name_in_use") {
          setD((s) => ({ ...s, alias: baselineRef.current.alias }));
        }
      }
    },
  });

  // F5: methodology Save & overwrite mutation. Fires the same PATCH
  // endpoint as autosave but with the full methodology-key surface so
  // the server invokes the §6 R&D round-trip path. On success the
  // confirm-modal has already closed; we update baseline + close the
  // drawer (the existing onClose).
  const methodologyM = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: ReturnType<typeof profileToUpdateInput>;
    }) => patchConfig(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: instrumentsKeys.configs });
      onClose();
    },
  });

  type MethodologyErrorKind =
    | "nfood_unreachable"
    | "nfood_rejected"
    | "validation"
    | "name_in_use"
    | "other";
  const methodologyError: { kind: MethodologyErrorKind } | null = useMemo(() => {
    if (!methodologyM.error) return null;
    if (axios.isAxiosError(methodologyM.error)) {
      const body = methodologyM.error.response?.data as
        | { error?: string }
        | undefined;
      const status = methodologyM.error.response?.status;
      if (body?.error === "name_in_use") return { kind: "name_in_use" };
      if (body?.error === "nfood_register_failed" && status === 502)
        return { kind: "nfood_unreachable" };
      if (body?.error === "nfood_register_failed" && status === 422)
        return { kind: "nfood_rejected" };
      if (status === 400) return { kind: "validation" };
    }
    return { kind: "other" };
  }, [methodologyM.error]);

  // F6: archive mutation. The trash icon opens DeleteConfigModal; the
  // modal's onConfirm fires this. On 204 success: invalidate configs +
  // orphans, close drawer. On 409 (chromatograms_attached): close the
  // confirm modal and switch to a "blocked" modal explaining the user
  // must detach chromatograms first.
  const archiveM = useMutation({
    mutationFn: (id: string) => archiveConfig(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: instrumentsKeys.configs });
      void qc.invalidateQueries({ queryKey: instrumentsKeys.orphans });
      // The default row may have referenced the just-archived config; the
      // page's picker re-fetches and clears the now-dangling pointer.
      void qc.invalidateQueries({ queryKey: instrumentsKeys.default });
      onClose();
    },
  });

  type ArchiveErrorKind = "chromatograms_attached" | "other";
  const archiveError: { kind: ArchiveErrorKind } | null = useMemo(() => {
    if (!archiveM.error) return null;
    if (axios.isAxiosError(archiveM.error)) {
      const body = archiveM.error.response?.data as
        | { error?: string }
        | undefined;
      if (body?.error === "chromatograms_attached")
        return { kind: "chromatograms_attached" };
    }
    return { kind: "other" };
  }, [archiveM.error]);

  // F8: per-chip tag attach / detach. Runs only in edit mode (configId
  // known). New tags not yet in the pool are created on the fly via a
  // direct createTag call (bypass useMutation for the inline await).
  // syncingTagCount tracks how many requests are in flight so the UI
  // can show a small "syncing tags…" pill while the user is rapidly
  // adding / removing chips.
  const [syncingTagCount, setSyncingTagCount] = useState(0);
  const isSyncingTags = syncingTagCount > 0;

  const syncTagsToServer = useCallback(
    async (prev: string[], next: string[]) => {
      if (!isEdit || !state.profile?.id) return;
      const configId = state.profile.id;
      const added = next.filter((t) => !prev.includes(t));
      const removed = prev.filter((t) => !next.includes(t));
      if (added.length === 0 && removed.length === 0) return;
      setSyncingTagCount((n) => n + added.length + removed.length);
      try {
        for (const text of added) {
          let tagId = tagPoolMap.get(text)?.id;
          if (!tagId) {
            // Tag isn't in the pool yet — create it inline so we have an
            // id before the attach. The pool invalidation in the parent
            // happens on the next render via the configs invalidation
            // below; until then we trust the response directly.
            const created = await createTag(text);
            tagId = created.id;
          }
          if (tagId) await attachTag(configId, tagId);
        }
        for (const text of removed) {
          const tagId = tagPoolMap.get(text)?.id;
          if (tagId) await detachTag(configId, tagId);
        }
      } finally {
        setSyncingTagCount(0);
        void qc.invalidateQueries({ queryKey: instrumentsKeys.tags });
        void qc.invalidateQueries({ queryKey: instrumentsKeys.configs });
      }
    },
    [isEdit, state, tagPoolMap, qc],
  );

  // Wrapped TagEditor onChange: update d.tags locally + fire the
  // server-side sync for the diff (edit mode only). New mode keeps tags
  // local; they're discarded on Register unless folded into the create
  // body via F3's tag_ids (currently disabled — re-add after register).
  const handleTagsChange = useCallback(
    (next: string[]) => {
      const prev = d.tags;
      update({ tags: next });
      void syncTagsToServer(prev, next);
    },
    [d.tags, syncTagsToServer],
  );

  const invalidateLadders = useCallback(() => {
    if (isEdit && state.profile?.id) {
      void qc.invalidateQueries({
        queryKey: instrumentsKeys.configLadders(state.profile.id),
      });
      // Refresh ladder_count badges on the cards list.
      void qc.invalidateQueries({ queryKey: instrumentsKeys.configs });
    }
  }, [isEdit, state, qc]);

  // F4 autosave effect — fires patchM on debounced alias/notes changes.
  useEffect(() => {
    if (!isEdit) return;
    if (!autoSaveDirty) return;
    if (nameTaken) return;
    if (!state.profile?.id) return;
    const configId = state.profile.id;
    const b = baselineRef.current;
    const patch: { name?: string; notes?: string | null } = {};
    if ((d.alias || "") !== (b.alias || "")) patch.name = d.alias.trim();
    if ((d.notes || "") !== (b.notes || "")) patch.notes = d.notes ?? null;
    if (Object.keys(patch).length === 0) return;
    const t = setTimeout(() => {
      patchM.mutate({ id: configId, patch });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.alias, d.notes, nameTaken, isEdit, state]);

  // Categorise the create error into a coarse kind. We deliberately do
  // NOT carry the upstream detail through — UI shows only sanitized
  // "...Failed. (CODE)" messages so raw R&D errors can't leak via DOM,
  // React DevTools, or any other surface.
  type CreateErrorKind =
    | "name_in_use"
    | "nfood_unreachable"
    | "nfood_rejected"
    | "validation"
    | "other";
  const createError: { kind: CreateErrorKind } | null = useMemo(() => {
    if (!createM.error) return null;
    if (axios.isAxiosError(createM.error)) {
      const body = createM.error.response?.data as
        | { error?: string }
        | undefined;
      const status = createM.error.response?.status;
      if (body?.error === "name_in_use") return { kind: "name_in_use" };
      if (body?.error === "nfood_register_failed" && status === 502)
        return { kind: "nfood_unreachable" };
      if (body?.error === "nfood_register_failed" && status === 422)
        return { kind: "nfood_rejected" };
      if (status === 400) return { kind: "validation" };
    }
    return { kind: "other" };
  }, [createM.error]);

  // F5: in edit mode the Save & overwrite confirm fires the methodology
  // mutation; the legacy onSave path is gone for edit-with-methodology.
  // (handleSave's non-edit path still uses createM for the F3 register
  // flow; edit-mode handleSave is never reachable from the UI today.)
  const commit = () => {
    if (isEdit && state.profile?.id) {
      methodologyM.reset();
      methodologyM.mutate({
        id: state.profile.id,
        patch: profileToUpdateInput({ ...d, alias: d.alias.trim() }),
      });
      return;
    }
    onSave({ ...d, alias: d.alias.trim() });
  };
  const handleSaveOverwrite = () => {
    if (!canSave) {
      setAttemptedSave(true);
      return;
    }
    setConfirmOpen(true);
  };
  const handleSave = () => {
    if (!canSave) {
      setAttemptedSave(true);
      return;
    }
    if (!isEdit) {
      // F3 — fire the server-side create instead of the legacy onSave callback.
      // Hand the draft alkane ladder file_upload_ids to the server so
      // registerOrEdit Stage 3b can map them onto the new config after
      // nfood register succeeds.
      const baseInput = profileToCreateInput({ ...d, alias: d.alias.trim() });
      const input = draftLadders.length
        ? {
            ...baseInput,
            alkane_ladder_draft_file_upload_ids: draftLadders.map(
              (l) => l.fileUploadId,
            ),
          }
        : baseInput;
      createM.reset();
      createM.mutate(input);
      return;
    }
    commit();
  };
  // Close the drawer + clean up any unfinalized draft ladder uploads.
  // Fire-and-forget the abandon call — the user is leaving the drawer
  // either way, and the server-side cleanup is idempotent. If it fails
  // (network blip), the rows linger as `status='uploaded'` with their
  // draft_id; nothing references them, but they're harmless until the
  // next pending-cleanup pass picks them up.
  //
  // Successful create reaches onSuccess → onClose directly, which means
  // the drafts have already been claimed onto the new config — don't
  // abandon them in that path.
  const handleCancel = () => {
    if (!isEdit && draftId && draftLadders.length > 0) {
      void abandonDraftLadders(draftId).catch(() => {
        // Sanitised — nothing actionable for the user.
      });
    }
    onClose();
  };

  // ── Save pill ──
  const savePill = (() => {
    if (!isEdit) return null;
    if (requiresManualSave) {
      return (
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            color: "#7c5813",
            padding: "2px 8px",
            borderRadius: 999,
            background: "var(--signal-soft)",
            border: "1px solid #e7c97a",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <Icon name="alert" size={9} />
          Unsaved
        </span>
      );
    }
    if (patchM.isPending) {
      return (
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            color: "var(--ink-3)",
            padding: "2px 8px",
            borderRadius: 999,
            background: "var(--bg-sunk)",
            border: "1px solid var(--line)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          Saving…
        </span>
      );
    }
    if (patchM.isError) {
      // Sanitised — no upstream detail surfaces. The 409 name_in_use
      // path additionally reverts the alias + shows the inline warning;
      // for everything else this pill is the only signal.
      return (
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            color: "#a14b3a",
            padding: "2px 8px",
            borderRadius: 999,
            background: "rgba(161, 75, 58, 0.08)",
            border: "1px solid rgba(161, 75, 58, 0.28)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <Icon name="alert" size={9} />
          Save failed
        </span>
      );
    }
    if (autoSavedTick > 0) {
      return (
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            color: "var(--brand-green)",
            padding: "2px 8px",
            borderRadius: 999,
            background: "var(--accent-soft)",
            border: "1px solid var(--accent-line)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          ● Saved · just now
        </span>
      );
    }
    return (
      <span
        className="mono"
        style={{
          fontSize: 9.5,
          color: "var(--ink-4)",
          padding: "2px 8px",
          borderRadius: 999,
          background: "var(--bg-sunk)",
          border: "1px solid var(--line)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          fontWeight: 700,
        }}
      >
        Saved
      </span>
    );
  })();

  return (
    <div
      onClick={handleCancel}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 220,
        background: "rgba(20,30,22,0.32)",
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="fade-in"
        style={{
          width: 620,
          maxWidth: "100%",
          background: "var(--panel)",
          borderLeft: "1px solid var(--line-strong)",
          boxShadow: "-12px 0 32px -12px rgba(20,30,22,0.18)",
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* TOP CHROME */}
        <div
          className="hstack"
          style={{
            padding: "10px 12px 10px 14px",
            borderBottom: "1px solid var(--line)",
            background: "var(--panel)",
            justifyContent: "space-between",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <div className="hstack" style={{ gap: 10 }}>
            {isEdit && onDelete && (
              <button
                className="btn xs"
                title="Delete configuration"
                onClick={() => setConfirmDelete(true)}
                style={{ color: "#a14b3a", padding: "5px 7px" }}
              >
                <Icon name="trash" size={12} />
              </button>
            )}
            <Slash>
              {isEdit
                ? "Experimental configuration · edit"
                : "Register · experimental configuration"}
            </Slash>
          </div>
          <div className="hstack" style={{ gap: 8 }}>
            {savePill}
            <button className="btn xs" onClick={handleCancel}>
              <Icon name="x" size={11} />
            </button>
          </div>
        </div>

        {/* SCROLLABLE BODY */}
        <div style={{ flex: 1, overflow: "auto", background: "var(--bg)" }}>
          {/* TITLE BLOCK */}
          <div
            style={{
              padding: "18px 24px 16px",
              background: "var(--panel)",
              borderBottom: "1px solid var(--line)",
            }}
          >
            <div
              className="hstack"
              style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}
            >
              <FieldLabel
                required
                satisfied={!!d.alias.trim()}
                showRequired={attemptedSave}
              >
                Configuration name
              </FieldLabel>
              {isEdit && <AutoSaveHint />}
            </div>
            <input
              value={d.alias}
              onChange={(e) => update({ alias: e.target.value })}
              placeholder="Name this configuration…"
              style={{
                width: "100%",
                padding: "4px 0 6px",
                border: "none",
                borderBottom: "1px solid transparent",
                background: "transparent",
                outline: "none",
                fontFamily: "var(--font-display)",
                fontSize: 22,
                fontWeight: 600,
                color: "var(--ink)",
                letterSpacing: "-0.005em",
              }}
              onFocus={(e) => (e.target.style.borderBottomColor = "var(--accent-line)")}
              onBlur={(e) => (e.target.style.borderBottomColor = "transparent")}
            />

            {(nameTaken || createError?.kind === "name_in_use") && (
              <div
                className="hstack"
                style={{ gap: 6, marginTop: 8, alignItems: "center" }}
              >
                <span
                  style={{
                    color: "#a14b3a",
                    display: "inline-flex",
                    flexShrink: 0,
                  }}
                >
                  <Icon name="alert" size={12} />
                </span>
                <span style={{ fontSize: 11.5, color: "#a14b3a", lineHeight: 1.4 }}>
                  A configuration named <strong>"{d.alias.trim()}"</strong> already exists —
                  please choose a different name.
                </span>
              </div>
            )}
          </div>

          {/* IDENTITY BAND */}
          <div
            style={{
              padding: "18px 24px 20px",
              background: "var(--panel)",
              borderBottom: "1px solid var(--line-2)",
            }}
          >
            <div className="vstack" style={{ gap: 16 }}>
              <div className="vstack" style={{ gap: 8 }}>
                <FieldLabel
                  required
                  satisfied={!!vendor}
                  showRequired={attemptedSave}
                >
                  Vendor
                </FieldLabel>
                <div className="hstack" style={{ gap: 8, flexWrap: "wrap" }}>
                  {INSTRUMENT_VENDORS.map((v) => {
                    const active = vendor === v.id;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => {
                          setVendor(v.id);
                          if (inst && inst.vendor !== v.id) update({ instrumentId: null });
                        }}
                        style={{
                          padding: "6px 12px",
                          borderRadius: 5,
                          fontSize: 12,
                          fontWeight: 500,
                          background: active ? "var(--brand-green)" : "var(--panel)",
                          color: active ? "var(--brand-cream)" : "var(--ink-2)",
                          border: `1px solid ${active ? "var(--brand-green)" : "var(--line-strong)"}`,
                          cursor: "pointer",
                        }}
                      >
                        {v.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="vstack" style={{ gap: 8 }}>
                <FieldLabel
                  required
                  satisfied={!!d.instrumentId}
                  showRequired={attemptedSave}
                  hint={inst ? `${inst.platform} · ${findAnalyser(inst.analyser)?.label}` : undefined}
                >
                  Model
                </FieldLabel>
                <Select
                  value={d.instrumentId}
                  onChange={(v) => update({ instrumentId: v === null ? null : Number(v) })}
                  options={instOptions}
                  placeholder={vendor ? `Choose a ${vendor} model…` : "Choose vendor first…"}
                />
              </div>

              <div
                style={{
                  height: 1,
                  background: "var(--line)",
                  margin: "2px 0",
                }}
              />

              <div className="vstack" style={{ gap: 6 }}>
                <FieldLabel
                  required
                  satisfied={!!d.column.id}
                  showRequired={attemptedSave}
                >
                  Column · Stationary phase
                </FieldLabel>
                <Select
                  value={d.column.id}
                  onChange={(v) => updateCol({ id: v === null ? null : Number(v) })}
                  options={colOptions}
                  placeholder="Choose stationary phase…"
                />
              </div>

              <div className="vstack" style={{ gap: 6 }}>
                <FieldLabel
                  required
                  satisfied={d.ms.energy_eV != null}
                  showRequired={attemptedSave}
                >
                  Mass spectrometer · Ionization energy
                </FieldLabel>
                <NumInput
                  value={d.ms.energy_eV}
                  onChange={(v) => updateMS({ energy_eV: v })}
                  suffix="eV"
                  width={90}
                  step={1}
                  placeholder="70"
                />
              </div>
            </div>
          </div>

          {/* ALKANE LADDER */}
          <PlainSection
            title="Alkane ladder"
            action={<AutoSaveHint />}
            badge={
              serverLadders.length > 0 && (
                <span
                  className="mono"
                  style={{
                    fontSize: 9,
                    padding: "1px 6px",
                    borderRadius: 2,
                    background: "var(--accent-soft)",
                    color: "var(--accent-ink)",
                    border: "1px solid var(--accent-line)",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  Calibrated
                </span>
              )
            }
          >
            {isEdit && state.profile?.id ? (
              <AlkaneLadderEditor
                mode="config"
                configId={state.profile.id}
                serverLadders={serverLadders}
                onMutated={invalidateLadders}
              />
            ) : (
              <AlkaneLadderEditor
                mode="draft"
                draftId={draftId}
                drafts={draftLadders}
                onDraftsChange={setDraftLadders}
              />
            )}
          </PlainSection>

          {/* TAGS */}
          <PlainSection
            title="Tags"
            action={<AutoSaveHint />}
            badge={
              isSyncingTags ? (
                <span
                  className="mono"
                  style={{
                    fontSize: 9,
                    padding: "1px 6px",
                    borderRadius: 2,
                    background: "var(--bg-sunk)",
                    color: "var(--ink-3)",
                    border: "1px solid var(--line)",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  Syncing…
                </span>
              ) : (
                d.tags.length > 0 && (
                  <span
                    className="mono"
                    style={{
                      fontSize: 9,
                      padding: "1px 6px",
                      borderRadius: 2,
                      background: "var(--bg-sunk)",
                      color: "var(--ink-3)",
                      border: "1px solid var(--line)",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    {d.tags.length} assigned
                  </span>
                )
              )
            }
          >
            <TagEditor
              value={d.tags}
              onChange={handleTagsChange}
              pool={tagPool}
              onCreateTag={onCreateTag}
            />
          </PlainSection>

          {/* NOTES */}
          <PlainSection title="Notes" action={<AutoSaveHint />}>
            <textarea
              value={d.notes}
              onChange={(e) => update({ notes: e.target.value })}
              placeholder="Calibration history, reservation requirements, shared-use caveats…"
              style={{
                width: "100%",
                minHeight: 70,
                padding: 10,
                borderRadius: 6,
                border: "1px solid var(--line-strong)",
                background: "var(--panel)",
                fontFamily: "var(--font-sans)",
                fontSize: 12.5,
                lineHeight: 1.55,
                resize: "vertical",
                outline: "none",
              }}
            />
          </PlainSection>

          {/* ADVANCED METHODOLOGY */}
          <AdvancedMethodologyFold>
            <Subsection title="Column · dimensions" first>
              <div className="vstack" style={{ gap: 6 }}>
                <FieldLabel>Length × i.d. × film</FieldLabel>
                <div className="hstack" style={{ gap: 6, flexWrap: "wrap" }}>
                  <NumInput
                    value={d.column.length_m}
                    onChange={(v) => updateCol({ length_m: v })}
                    suffix="m"
                    width={64}
                    step={1}
                    placeholder="30"
                  />
                  <span style={{ color: "var(--ink-4)" }}>×</span>
                  <NumInput
                    value={d.column.id_mm}
                    onChange={(v) => updateCol({ id_mm: v })}
                    suffix="mm i.d."
                    width={64}
                    step={0.05}
                    placeholder="0.25"
                  />
                  <span style={{ color: "var(--ink-4)" }}>×</span>
                  <NumInput
                    value={d.column.film_um}
                    onChange={(v) => updateCol({ film_um: v })}
                    suffix="µm film"
                    width={64}
                    step={0.05}
                    placeholder="0.25"
                  />
                </div>
              </div>
            </Subsection>

            <Subsection title="Mass spectrometer · detail">
              <div className="vstack" style={{ gap: 14 }}>
                <div className="vstack" style={{ gap: 6 }}>
                  <FieldLabel>Ionization technique</FieldLabel>
                  <div className="hstack" style={{ gap: 6 }}>
                    {IONIZATIONS.map((io) => {
                      const active = d.ms.ionization === io;
                      return (
                        <button
                          key={io}
                          type="button"
                          onClick={() => updateMS({ ionization: io })}
                          style={{
                            padding: "5px 10px",
                            borderRadius: 4,
                            fontSize: 12,
                            fontFamily: "var(--font-mono)",
                            background: active ? "var(--brand-green)" : "var(--panel)",
                            color: active ? "var(--brand-cream)" : "var(--ink-2)",
                            border: `1px solid ${
                              active ? "var(--brand-green)" : "var(--line-strong)"
                            }`,
                            cursor: "pointer",
                          }}
                        >
                          {io}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="vstack" style={{ gap: 6 }}>
                  <FieldLabel>Ion source temperature</FieldLabel>
                  <NumInput
                    value={d.ms.sourceTemp_C}
                    onChange={(v) => updateMS({ sourceTemp_C: v })}
                    suffix="°C"
                    width={80}
                    step={1}
                    placeholder="230"
                  />
                </div>
                <div className="vstack" style={{ gap: 6 }}>
                  <FieldLabel>Scan range</FieldLabel>
                  <div className="hstack" style={{ gap: 6 }}>
                    <NumInput
                      value={d.ms.scanLo}
                      onChange={(v) => updateMS({ scanLo: v })}
                      width={64}
                      step={1}
                      placeholder="35"
                    />
                    <span style={{ color: "var(--ink-4)" }}>→</span>
                    <NumInput
                      value={d.ms.scanHi}
                      onChange={(v) => updateMS({ scanHi: v })}
                      suffix="m/z"
                      width={80}
                      step={1}
                      placeholder="450"
                    />
                  </div>
                </div>
                <div className="vstack" style={{ gap: 6 }}>
                  <FieldLabel>Scan mode</FieldLabel>
                  <Select
                    value={d.ms.mode}
                    onChange={(v) => updateMS({ mode: v })}
                    options={SCAN_MODES.map((m) => ({ value: m, label: m }))}
                    placeholder="Choose scan mode…"
                    width={260}
                  />
                </div>
              </div>
            </Subsection>

            <Subsection title="Inlet & flow">
              <div className="vstack" style={{ gap: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div className="vstack" style={{ gap: 6 }}>
                    <FieldLabel>Carrier gas</FieldLabel>
                    <Select
                      value={d.inlet.carrier}
                      onChange={(v) => updateInlet({ carrier: v as CarrierGas | null })}
                      options={CARRIERS.map((g) => ({ value: g, label: g }))}
                      placeholder="Choose carrier gas…"
                      width={160}
                    />
                  </div>
                  <div className="vstack" style={{ gap: 6 }}>
                    <FieldLabel>Column flow</FieldLabel>
                    <NumInput
                      value={d.inlet.flow_ml_min}
                      onChange={(v) => updateInlet({ flow_ml_min: v })}
                      suffix="mL/min"
                      width={90}
                      placeholder="1.2"
                    />
                  </div>
                  <div className="vstack" style={{ gap: 6 }}>
                    <FieldLabel>Inlet temperature</FieldLabel>
                    <NumInput
                      value={d.inlet.temp_C}
                      onChange={(v) => updateInlet({ temp_C: v })}
                      suffix="°C"
                      width={90}
                      step={1}
                      placeholder="250"
                    />
                  </div>
                  <div className="vstack" style={{ gap: 6 }}>
                    <FieldLabel>Injection volume</FieldLabel>
                    <NumInput
                      value={d.inlet.vol_uL}
                      onChange={(v) => updateInlet({ vol_uL: v })}
                      suffix="µL"
                      width={80}
                      step={0.1}
                      placeholder="1.0"
                    />
                  </div>
                </div>
                <div className="vstack" style={{ gap: 6 }}>
                  <FieldLabel>Injection mode</FieldLabel>
                  <Select
                    value={d.inlet.mode}
                    onChange={(v) => updateInlet({ mode: v as InjectionMode | null })}
                    options={INJECTION_MODES.map((m) => ({ value: m, label: m }))}
                    placeholder="Choose injection mode…"
                    width={220}
                  />
                </div>
              </div>
            </Subsection>

            <Subsection title="Oven program">
              <OvenEditor program={d.oven} onChange={(p) => update({ oven: p })} />
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  color: "var(--ink-4)",
                  marginTop: 10,
                  padding: "8px 10px",
                  borderRadius: 5,
                  background: "var(--bg-sunk)",
                  border: "1px solid var(--line-2)",
                  lineHeight: 1.5,
                }}
              >
                Preview · {formatOven(d.oven)}
              </div>
            </Subsection>

            <Subsection title="Methodology">
              <div className="vstack" style={{ gap: 6 }}>
                <FieldLabel hint="Sample prep, derivatization, daily-run quirks — anything the analyst should know.">
                  Method specification
                </FieldLabel>
                <textarea
                  value={d.methodology?.notes ?? ""}
                  onChange={(e) => updateMeth({ notes: e.target.value })}
                  placeholder={`Describe how this method is run, e.g.:

• Sample prep — 5 g homogenate, saline matrix, centrifuged 10 min @ 4 °C
• Extraction — SPME, DVB/CAR/PDMS fiber, 60 min @ 50 °C, 250 rpm
• Derivatization — none / BSTFA + 1% TMCS, 30 min @ 60 °C
• Run quirks — solvent delay 3.0 min, fresh septum every 50 injections, daily tune required, etc.`}
                  style={{
                    width: "100%",
                    minHeight: 180,
                    padding: 12,
                    borderRadius: 6,
                    border: "1px solid var(--line-strong)",
                    background: "var(--panel)",
                    fontFamily: "var(--font-sans)",
                    fontSize: 12.5,
                    lineHeight: 1.6,
                    resize: "vertical",
                    outline: "none",
                  }}
                />
              </div>
            </Subsection>
          </AdvancedMethodologyFold>

          <div style={{ height: 16 }} />
        </div>

        {/* STICKY FOOTER */}
        <div
          className="hstack"
          style={{
            padding: "12px 22px",
            borderTop: "1px solid var(--line)",
            background: requiresManualSave ? "rgba(231, 201, 122, 0.10)" : "var(--panel-2)",
            justifyContent: "space-between",
            gap: 12,
            flexShrink: 0,
            transition: "background 160ms ease",
          }}
        >
          <span
            className="mono"
            style={{
              fontSize: 10,
              color: requiresManualSave ? "#7c5813" : "var(--ink-4)",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            {methodologyM.isPending ? (
              <>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: "var(--brand-green)",
                    animation: "pulse 1.2s ease-in-out infinite",
                  }}
                />
                Saving & re-registering…
              </>
            ) : methodologyError?.kind === "nfood_unreachable" ? (
              <span style={{ color: "#a14b3a", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Icon name="alert" size={10} />
                Save failed. (SERVICE_UNAVAILABLE) — tap Save & overwrite to retry
              </span>
            ) : methodologyError?.kind === "nfood_rejected" ? (
              <span style={{ color: "#a14b3a", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Icon name="alert" size={10} />
                Save failed. (REGISTRATION_REJECTED)
              </span>
            ) : methodologyError?.kind === "name_in_use" ? (
              <span style={{ color: "#a14b3a", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Icon name="alert" size={10} />
                Save failed. (NAME_IN_USE) — change the name first
              </span>
            ) : methodologyError?.kind === "validation" ? (
              <span style={{ color: "#a14b3a", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Icon name="alert" size={10} />
                Save failed. (BAD_REQUEST)
              </span>
            ) : methodologyError?.kind === "other" ? (
              <span style={{ color: "#a14b3a", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Icon name="alert" size={10} />
                Save failed. — tap Save & overwrite to retry
              </span>
            ) : requiresManualSave ? (
              <>
                <Icon name="alert" size={10} />
                Methodology edited
                {hasFiles
                  ? ` · ${fileCount} attached file${fileCount === 1 ? "" : "s"} will be re-analyzed on overwrite`
                  : " — choose how to save"}
              </>
            ) : !isEdit && createM.isPending ? (
              <>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: "var(--brand-green)",
                    animation: "pulse 1.2s ease-in-out infinite",
                  }}
                />
                Registering…
              </>
            ) : !isEdit && createError?.kind === "nfood_unreachable" ? (
              <span style={{ color: "#a14b3a", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Icon name="alert" size={10} />
                Registration failed. (SERVICE_UNAVAILABLE) — tap Register to retry
              </span>
            ) : !isEdit && createError?.kind === "nfood_rejected" ? (
              <span style={{ color: "#a14b3a", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Icon name="alert" size={10} />
                Registration failed. (REGISTRATION_REJECTED)
              </span>
            ) : !isEdit && createError?.kind === "validation" ? (
              <span style={{ color: "#a14b3a", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Icon name="alert" size={10} />
                Registration failed. (BAD_REQUEST)
              </span>
            ) : !isEdit && createError?.kind === "other" ? (
              <span style={{ color: "#a14b3a", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Icon name="alert" size={10} />
                Registration failed. — tap Register to retry
              </span>
            ) : !isEdit ? (
              "Required fields marked ✱"
            ) : (
              "Name, notes & ladder auto-save · methodology requires explicit save"
            )}
          </span>
          <div className="hstack" style={{ gap: 8 }}>
            {isEdit && requiresManualSave ? (
              <>
                <button
                  className="btn sm"
                  onClick={handleCancel}
                  disabled={methodologyM.isPending}
                  style={{ opacity: methodologyM.isPending ? 0.55 : 1 }}
                >
                  Discard changes
                </button>
                <button
                  className="btn primary sm"
                  onClick={handleSaveOverwrite}
                  disabled={methodologyM.isPending}
                  style={{
                    opacity: canSave && !methodologyM.isPending ? 1 : 0.55,
                  }}
                >
                  {methodologyM.isPending ? "Saving…" : "Save & overwrite"}
                  <Icon name="arrow-r" size={12} />
                </button>
              </>
            ) : isEdit ? (
              <button className="btn sm" onClick={handleCancel}>
                Close
              </button>
            ) : (
              <>
                <button
                  className="btn sm"
                  onClick={handleCancel}
                  disabled={createM.isPending}
                  style={{ opacity: createM.isPending ? 0.55 : 1 }}
                >
                  Cancel
                </button>
                <button
                  className="btn primary sm"
                  onClick={handleSave}
                  disabled={createM.isPending}
                  style={{
                    opacity: canSave && !createM.isPending ? 1 : 0.55,
                  }}
                >
                  {createM.isPending ? "Registering…" : "Register configuration"}
                  <Icon name="arrow-r" size={12} />
                </button>
              </>
            )}
          </div>
        </div>

        {confirmOpen && (
          <SaveConfirmModal
            alias={d.alias}
            fileCount={fileCount}
            onCancel={() => setConfirmOpen(false)}
            onConfirm={() => {
              setConfirmOpen(false);
              commit();
            }}
          />
        )}
        {confirmDelete && isEdit && state.profile?.id && !archiveError && (
          <DeleteConfigModal
            alias={d.alias}
            fileCount={fileCount}
            pending={archiveM.isPending}
            onCancel={() => {
              if (archiveM.isPending) return;
              setConfirmDelete(false);
            }}
            onConfirm={() => {
              archiveM.reset();
              archiveM.mutate(state.profile.id);
            }}
          />
        )}
        {archiveError?.kind === "chromatograms_attached" && (
          <ArchiveBlockedModal
            alias={d.alias}
            onDismiss={() => {
              archiveM.reset();
              setConfirmDelete(false);
            }}
          />
        )}
        {isEdit && archiveError?.kind === "other" && (
          <ArchiveFailedModal
            onRetry={() => {
              archiveM.reset();
              if (state.profile?.id) archiveM.mutate(state.profile.id);
            }}
            onDismiss={() => {
              archiveM.reset();
              setConfirmDelete(false);
            }}
          />
        )}
      </aside>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function Slash({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        color: "var(--ink-3)",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        fontWeight: 700,
      }}
    >
      {children}
    </span>
  );
}

function AutoSaveHint() {
  return (
    <span
      style={{
        fontSize: 11,
        color: "var(--ink-4)",
        fontStyle: "italic",
        whiteSpace: "nowrap",
        fontWeight: 400,
      }}
    >
      auto-saves
    </span>
  );
}

function FieldLabel({
  children,
  hint,
  required,
  satisfied,
  showRequired,
}: {
  children: React.ReactNode;
  hint?: string;
  required?: boolean;
  satisfied?: boolean;
  showRequired?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span
        className="mono"
        style={{
          fontSize: 9.5,
          color: "var(--ink-4)",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          fontWeight: 600,
        }}
      >
        {children}
        {required && <span style={{ color: "#a14b3a", marginLeft: 3 }}>✱</span>}
      </span>
      {hint && <span style={{ fontSize: 10.5, color: "var(--ink-4)" }}>· {hint}</span>}
      {required && !satisfied && showRequired && (
        <span
          className="mono"
          style={{
            fontSize: 9,
            padding: "1px 6px",
            borderRadius: 999,
            background: "rgba(161, 75, 58, 0.08)",
            color: "#a14b3a",
            border: "1px solid rgba(161, 75, 58, 0.28)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          Required
        </span>
      )}
    </div>
  );
}

function PlainSection({
  title,
  badge,
  action,
  children,
}: {
  title: string;
  badge?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section style={{ borderTop: "1px solid var(--line)" }}>
      <header
        style={{
          padding: "11px 24px",
          background: "var(--bg-sunk)",
          borderBottom: "1px solid var(--line)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <div className="hstack" style={{ gap: 8 }}>
          <span
            className="mono"
            style={{
              fontSize: 10.5,
              color: "var(--ink)",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
          >
            {title}
          </span>
          {badge}
        </div>
        {action && <div>{action}</div>}
      </header>
      <div style={{ padding: "16px 24px 22px", background: "var(--panel)" }}>{children}</div>
    </section>
  );
}

function AdvancedMethodologyFold({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <section style={{ borderTop: "1px solid var(--line)" }}>
      <header
        onClick={() => setOpen((o) => !o)}
        style={{
          padding: "14px 24px",
          cursor: "pointer",
          background: open ? "var(--bg-sunk)" : "var(--panel)",
          borderBottom: open ? "1px solid var(--line)" : "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          transition: "background 140ms ease",
        }}
      >
        <div className="hstack" style={{ gap: 10, alignItems: "center" }}>
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 5,
              flexShrink: 0,
              background: open ? "var(--accent-soft)" : "var(--bg-sunk)",
              border: `1px solid ${open ? "var(--accent-line)" : "var(--line-strong)"}`,
              color: open ? "var(--accent-ink)" : "var(--ink-3)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 140ms, border-color 140ms, color 140ms",
              transform: open ? "rotate(90deg)" : "none",
            }}
          >
            <Icon name="chevron-r" size={12} />
          </span>
          <div className="vstack" style={{ gap: 1, lineHeight: 1.25 }}>
            <span
              className="mono"
              style={{
                fontSize: 11,
                color: "var(--ink)",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              Advanced methodology
            </span>
            <span style={{ fontSize: 11.5, color: "var(--ink-4)" }}>
              {open
                ? "Column dimensions, MS scan, inlet, oven program, free-text method"
                : "Show optional method parameters"}
            </span>
          </div>
        </div>
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            padding: "2px 8px",
            borderRadius: 999,
            background: open ? "var(--accent-soft)" : "var(--bg-sunk)",
            border: `1px solid ${open ? "var(--accent-line)" : "var(--line)"}`,
            color: open ? "var(--accent-ink)" : "var(--ink-4)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 700,
          }}
        >
          {open ? "Open" : "Optional"}
        </span>
      </header>
      {open && <div>{children}</div>}
    </section>
  );
}

function Subsection({
  title,
  children,
  first = false,
}: {
  title: string;
  children: React.ReactNode;
  first?: boolean;
}) {
  return (
    <div
      style={{
        padding: "16px 24px 20px",
        borderTop: first ? "none" : "1px solid var(--line-2)",
        background: "var(--panel)",
      }}
    >
      <div
        className="mono"
        style={{
          fontSize: 9.5,
          color: "var(--ink-3)",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontWeight: 600,
          marginBottom: 14,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function NumInput({
  value,
  onChange,
  suffix,
  width = 90,
  step = 0.1,
  placeholder,
}: {
  value: number | null | undefined;
  onChange: (n: number | null) => void;
  suffix?: string;
  width?: number;
  step?: number;
  placeholder?: string;
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <input
        className="mono"
        type="number"
        step={step}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))}
        style={{
          width,
          padding: "5px 8px",
          borderRadius: 5,
          border: "1px solid var(--line-strong)",
          background: "var(--panel)",
          fontFamily: "var(--font-mono)",
          color: "var(--ink)",
          textAlign: "right",
          fontSize: 12,
          fontFeatureSettings: "'tnum'",
          outline: "none",
        }}
      />
      {suffix && (
        <span className="mono" style={{ fontSize: 11, color: "var(--ink-4)" }}>
          {suffix}
        </span>
      )}
    </span>
  );
}

type SelectOption<V> = { value: V; label: string };

// Custom dropdown that replaces the native <select> browser chrome. Mirrors
// the design's PlainSelect: bordered trigger + caret, portaled popover that
// flips above when there's no room below, hover/active/selected row states
// (selected row gets the left-rail accent + check), keyboard navigation
// (↑/↓ to move, Enter to pick, Esc to close).
function Select<V extends string | number | null>({
  value,
  onChange,
  options,
  placeholder = "Select…",
  width,
}: {
  value: V;
  onChange: (v: V | null) => void;
  options: SelectOption<NonNullable<V>>[];
  placeholder?: string;
  width?: number | string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<{
    left: number;
    top: number | undefined;
    bottom: number | undefined;
    width: number;
    maxHeight: number;
  } | null>(null);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  const sel = options.find((o) => String(o.value) === String(value)) ?? null;

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        !wrapRef.current?.contains(t) &&
        !popRef.current?.contains(t)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, options.length - 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
      }
      if (e.key === "Enter" && active >= 0 && options[active]) {
        e.preventDefault();
        onChange(options[active].value);
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, active, options, onChange]);

  // Decide flip direction + dimensions when the popover opens.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const flipUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    const maxH = Math.max(180, Math.min(320, flipUp ? spaceAbove - 16 : spaceBelow - 16));
    setPos({
      left: r.left,
      width: r.width,
      maxHeight: maxH,
      top: flipUp ? undefined : r.bottom + 4,
      bottom: flipUp ? window.innerHeight - r.top + 4 : undefined,
    });
    setActive(options.findIndex((o) => String(o.value) === String(value)));
  }, [open, options, value]);

  const pick = (v: NonNullable<V>) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", width: width ?? "100%" }}>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if ((e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 9px",
          minHeight: 30,
          borderRadius: 5,
          border: `1px solid ${open ? "var(--ink)" : "var(--line-strong)"}`,
          background: "var(--panel)",
          boxShadow: open ? "0 0 0 2px var(--accent-soft)" : "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "border-color 120ms, box-shadow 120ms",
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12.5,
            color: sel ? "var(--ink)" : "var(--ink-4)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {sel ? sel.label : placeholder}
        </span>
        <span
          style={{
            color: "var(--ink-3)",
            display: "inline-flex",
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 160ms ease",
          }}
        >
          <Icon name="chevron" size={11} />
        </span>
      </button>

      {open && pos &&
        createPortal(
          <div
            ref={popRef}
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              bottom: pos.bottom,
              width: pos.width,
              zIndex: 1000,
              background: "var(--panel)",
              border: "1px solid var(--line-strong)",
              borderRadius: 8,
              boxShadow: "0 12px 32px -8px rgba(20,30,22,0.22)",
              maxHeight: pos.maxHeight,
              overflow: "auto",
              padding: "2px 0",
            }}
          >
            {options.map((o, i) => {
              const isSel = String(o.value) === String(value);
              const isActive = i === active;
              return (
                <button
                  key={String(o.value)}
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(o.value)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "7px 12px",
                    border: "none",
                    textAlign: "left",
                    background: isSel
                      ? "var(--accent-soft)"
                      : isActive
                      ? "var(--bg-sunk)"
                      : "transparent",
                    borderLeft: `3px solid ${isSel ? "var(--accent, var(--brand-green))" : "transparent"}`,
                    cursor: "pointer",
                    color: "inherit",
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 12.5,
                      color: isSel ? "var(--accent-ink)" : "var(--ink-2)",
                      fontWeight: isSel ? 500 : 400,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {o.label}
                  </span>
                  {isSel && (
                    <span
                      style={{ color: "var(--accent-ink)", display: "flex", flexShrink: 0 }}
                    >
                      <Icon name="check" size={12} />
                    </span>
                  )}
                </button>
              );
            })}
            {options.length === 0 && (
              <div
                style={{
                  padding: "12px 14px",
                  fontSize: 11.5,
                  color: "var(--ink-4)",
                  fontStyle: "italic",
                  textAlign: "center",
                }}
              >
                Nothing to choose yet.
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

function OvenEditor({
  program,
  onChange,
}: {
  program: OvenStep[];
  onChange: (next: OvenStep[]) => void;
}) {
  const update = (i: number, patch: Partial<OvenStep>) =>
    onChange(program.map((s, idx) => (idx === i ? ({ ...s, ...patch } as OvenStep) : s)));
  // New ramps start with null fields so the placeholder hints show through
  // and the user has to actually type a value for it to be submitted.
  const addRamp = () =>
    onChange([...program, { kind: "ramp", rate: null, target: null, hold: null }]);
  const removeRamp = (i: number) => onChange(program.filter((_, idx) => idx !== i));

  return (
    <div className="vstack" style={{ gap: 8 }}>
      {program.map((step, i) => (
        <div
          key={i}
          className="hstack"
          style={{
            gap: 10,
            padding: "8px 10px",
            background: "var(--bg-sunk)",
            border: "1px solid var(--line)",
            borderRadius: 5,
            flexWrap: "wrap",
          }}
        >
          <span
            className="mono"
            style={{
              fontSize: 10,
              color: "var(--ink-3)",
              padding: "2px 6px",
              borderRadius: 3,
              background: "var(--panel)",
              border: "1px solid var(--line)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              width: 56,
              textAlign: "center",
              flexShrink: 0,
            }}
          >
            {step.kind === "initial" ? `Init.` : `Ramp ${i}`}
          </span>

          {step.kind === "ramp" && (
            <>
              <NumInput
                value={step.rate}
                onChange={(v) => update(i, { rate: v })}
                suffix="°/min"
                width={64}
                step={0.5}
                placeholder="10"
              />
              <span style={{ color: "var(--ink-4)" }}>→</span>
            </>
          )}
          <NumInput
            value={step.kind === "initial" ? step.temp : step.target}
            onChange={(v) =>
              update(i, step.kind === "initial" ? { temp: v } : { target: v })
            }
            suffix="°C"
            width={64}
            step={1}
            placeholder={step.kind === "initial" ? "40" : "240"}
          />
          <span style={{ color: "var(--ink-4)" }}>hold</span>
          <NumInput
            value={step.hold}
            onChange={(v) => update(i, { hold: v })}
            suffix="min"
            width={56}
            step={0.5}
            placeholder="0"
          />

          <div style={{ flex: 1 }} />
          {step.kind === "ramp" && (
            <button
              className="btn xs ghost"
              onClick={() => removeRamp(i)}
              title="Remove ramp"
            >
              <Icon name="x" size={10} />
            </button>
          )}
        </div>
      ))}
      <button
        className="btn xs"
        onClick={addRamp}
        style={{ alignSelf: "flex-start", marginTop: 4 }}
      >
        <Icon name="plus" size={11} />
        Add ramp
      </button>
    </div>
  );
}


function TagEditor({
  value,
  onChange,
  pool,
  onCreateTag,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  pool: string[];
  onCreateTag?: (tag: string) => void;
}) {
  const [input, setInput] = useState("");
  const available = pool.filter((t) => !value.includes(t));

  const addTag = (t: string) => {
    const trimmed = t.trim();
    if (!trimmed) return;
    if (!pool.some((x) => x.toLowerCase() === trimmed.toLowerCase())) {
      onCreateTag?.(trimmed);
    }
    if (!value.some((x) => x.toLowerCase() === trimmed.toLowerCase())) {
      onChange([...value, trimmed]);
    }
    setInput("");
  };

  return (
    <div className="vstack" style={{ gap: 10 }}>
      <div className="hstack" style={{ gap: 6, flexWrap: "wrap" }}>
        {value.length === 0 && (
          <span
            className="mono"
            style={{ fontSize: 11, color: "var(--ink-4)", fontStyle: "italic" }}
          >
            No tags assigned yet.
          </span>
        )}
        {value.map((t) => (
          <span
            key={t}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 10px",
              borderRadius: 999,
              background: "var(--accent-soft)",
              color: "var(--accent-ink)",
              border: "1px solid var(--accent-line)",
              fontSize: 11.5,
              fontWeight: 500,
            }}
          >
            {t}
            <button
              type="button"
              onClick={() => onChange(value.filter((x) => x !== t))}
              style={{
                background: "transparent",
                border: 0,
                cursor: "pointer",
                color: "var(--accent-ink)",
                padding: 0,
                display: "inline-flex",
              }}
            >
              <Icon name="x" size={10} />
            </button>
          </span>
        ))}
      </div>

      <div className="hstack" style={{ gap: 6 }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag(input);
            }
          }}
          placeholder="Add a tag…"
          style={{
            flex: 1,
            padding: "6px 10px",
            borderRadius: 5,
            border: "1px solid var(--line-strong)",
            background: "var(--panel)",
            fontSize: 12,
            color: "var(--ink)",
            outline: "none",
          }}
        />
        <button className="btn xs" onClick={() => addTag(input)} disabled={!input.trim()}>
          <Icon name="plus" size={11} />
          Add
        </button>
      </div>

      {available.length > 0 && (
        <div className="hstack" style={{ gap: 6, flexWrap: "wrap" }}>
          <span
            className="mono"
            style={{
              fontSize: 9.5,
              color: "var(--ink-4)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontWeight: 600,
              marginRight: 4,
            }}
          >
            From pool
          </span>
          {available.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onChange([...value, t])}
              style={{
                padding: "3px 10px",
                borderRadius: 999,
                background: "var(--bg-sunk)",
                color: "var(--ink-2)",
                border: "1px solid var(--line)",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              + {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Confirmation modals ────────────────────────────────────────────

function SaveConfirmModal({
  alias,
  fileCount,
  onCancel,
  onConfirm,
}: {
  alias: string;
  fileCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      onClick={onCancel}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 280,
        background: "rgba(20, 30, 22, 0.42)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 480,
          background: "var(--panel)",
          borderRadius: 12,
          boxShadow: "0 16px 48px -12px rgba(20,30,22,0.32)",
          border: "1px solid var(--line-strong)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "18px 22px 14px" }}>
          <div className="hstack" style={{ gap: 10, marginBottom: 12, alignItems: "flex-start" }}>
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                flexShrink: 0,
                background: "rgba(124, 88, 19, 0.12)",
                color: "#7c5813",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="alert" size={15} />
            </span>
            <div className="vstack" style={{ gap: 2 }}>
              <Slash>Save &amp; overwrite</Slash>
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "var(--ink)",
                  fontFamily: "var(--font-display)",
                  letterSpacing: "-0.005em",
                }}
              >
                Overwrite{" "}
                <em style={{ fontStyle: "italic", color: "var(--ink-2)" }}>
                  "{alias || "this configuration"}"
                </em>
                ?
              </span>
            </div>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "var(--ink-3)", lineHeight: 1.6 }}>
            <strong style={{ color: "var(--ink-2)" }}>Save &amp; overwrite</strong> replaces the
            saved methodology for this configuration with your edits. The previous values are{" "}
            <strong style={{ color: "var(--ink-2)" }}>not recoverable</strong>. Future
            chromatograms uploaded against this configuration will use the new method.
          </p>
          {fileCount > 0 ? (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                borderRadius: 6,
                background: "var(--signal-soft)",
                border: "1px solid #e7c97a",
                color: "#7c5813",
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                fontSize: 11.5,
                lineHeight: 1.55,
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 2 }}>
                <Icon name="alert" size={12} />
              </span>
              <span>
                <strong>{fileCount}</strong> file{fileCount === 1 ? "" : "s"} attached to this
                configuration will be <strong>re-analyzed</strong>: retention indices, peak
                picks, and ingredient links may shift. You'll get a diff summary once
                re-analysis completes (~5–8 min).
              </span>
            </div>
          ) : (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                borderRadius: 6,
                background: "var(--bg-sunk)",
                border: "1px solid var(--line-2)",
                color: "var(--ink-3)",
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                fontSize: 11.5,
                lineHeight: 1.55,
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 2, color: "var(--ink-4)" }}>
                <Icon name="info" size={12} />
              </span>
              <span>
                No files are attached yet, so nothing needs to be re-analyzed — this is a
                straightforward replacement of the saved method.
              </span>
            </div>
          )}
        </div>
        <div
          className="hstack"
          style={{
            padding: "12px 22px",
            background: "var(--panel-2)",
            borderTop: "1px solid var(--line)",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn primary" onClick={onConfirm}>
            {fileCount > 0 ? "Save & re-analyze" : "Save & overwrite"}
            <Icon name="arrow-r" size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfigModal({
  alias,
  fileCount,
  pending = false,
  onCancel,
  onConfirm,
}: {
  alias: string;
  fileCount: number;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      onClick={onCancel}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 280,
        background: "rgba(20, 30, 22, 0.42)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          background: "var(--panel)",
          borderRadius: 12,
          boxShadow: "0 16px 48px -12px rgba(20,30,22,0.32)",
          border: "1px solid var(--line-strong)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "18px 22px 14px" }}>
          <div className="hstack" style={{ gap: 10, marginBottom: 12, alignItems: "flex-start" }}>
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                flexShrink: 0,
                background: "rgba(161, 75, 58, 0.12)",
                color: "#a14b3a",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="trash" size={15} />
            </span>
            <div className="vstack" style={{ gap: 2 }}>
              <Slash>Delete configuration</Slash>
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "var(--ink)",
                  fontFamily: "var(--font-display)",
                  letterSpacing: "-0.005em",
                }}
              >
                Delete{" "}
                <em style={{ fontStyle: "italic", color: "var(--ink-2)" }}>
                  "{alias || "this configuration"}"
                </em>
                ?
              </span>
            </div>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "var(--ink-3)", lineHeight: 1.55 }}>
            The methodology, column, and MS settings will be removed. Any chromatograms uploaded
            against this configuration are detached and will need to be re-attributed to another
            method before they can be re-analyzed.
          </p>
          {fileCount > 0 && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                borderRadius: 6,
                background: "var(--signal-soft)",
                border: "1px solid #e7c97a",
                color: "#7c5813",
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                fontSize: 11.5,
                lineHeight: 1.55,
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 2 }}>
                <Icon name="alert" size={12} />
              </span>
              <span>
                <strong>{fileCount}</strong> file{fileCount === 1 ? "" : "s"} attached to this
                configuration will be orphaned. They keep their raw data but lose their
                retention-index calibration.
              </span>
            </div>
          )}
        </div>
        <div
          className="hstack"
          style={{
            padding: "12px 22px",
            background: "var(--panel-2)",
            borderTop: "1px solid var(--line)",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button className="btn" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          <button
            onClick={pending ? undefined : onConfirm}
            disabled={pending}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              fontSize: 13,
              background: "#a14b3a",
              color: "#fff",
              border: "1px solid #a14b3a",
              borderRadius: 5,
              cursor: pending ? "not-allowed" : "pointer",
              opacity: pending ? 0.55 : 1,
              fontFamily: "inherit",
              fontWeight: 500,
            }}
          >
            <Icon name="trash" size={12} />
            {pending ? "Archiving…" : "Delete configuration"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Shown when the archive request returns 409 chromatograms_attached.
// The user has to detach the attached chromatograms before they can
// archive this configuration; messages are sanitised and contain no
// internal jargon.
function ArchiveBlockedModal({
  alias,
  onDismiss,
}: {
  alias: string;
  onDismiss: () => void;
}) {
  return (
    <div
      onClick={onDismiss}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 280,
        background: "rgba(20, 30, 22, 0.42)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 440,
          background: "var(--panel)",
          borderRadius: 8,
          border: "1px solid var(--line-strong)",
          boxShadow: "0 12px 36px -6px rgba(20,30,22,0.32)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "18px 22px 8px" }}>
          <Slash>Can't delete · chromatograms attached</Slash>
          <div
            className="display"
            style={{
              fontSize: 18,
              color: "var(--ink)",
              letterSpacing: "-0.005em",
              marginTop: 10,
              lineHeight: 1.35,
            }}
          >
            <strong>"{alias}"</strong> has chromatograms attached to it.
          </div>
          <p
            style={{
              marginTop: 10,
              fontSize: 12.5,
              color: "var(--ink-2)",
              lineHeight: 1.55,
            }}
          >
            Detach (or move) every chromatogram pinned to this configuration
            first, then try deleting it again.
          </p>
        </div>
        <div
          className="hstack"
          style={{
            padding: "12px 22px",
            background: "var(--panel-2)",
            borderTop: "1px solid var(--line)",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button className="btn primary" onClick={onDismiss}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

// Generic archive-failed modal. Used for any non-409 archive error;
// sanitised — no upstream / status detail rendered.
function ArchiveFailedModal({
  onRetry,
  onDismiss,
}: {
  onRetry: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      onClick={onDismiss}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 280,
        background: "rgba(20, 30, 22, 0.42)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 380,
          background: "var(--panel)",
          borderRadius: 8,
          border: "1px solid var(--line-strong)",
          boxShadow: "0 12px 36px -6px rgba(20,30,22,0.32)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "18px 22px 8px" }}>
          <Slash>Delete failed</Slash>
          <p
            style={{
              marginTop: 10,
              fontSize: 12.5,
              color: "var(--ink-2)",
              lineHeight: 1.55,
            }}
          >
            We couldn't delete this configuration right now. You can try
            again or close this dialog and try later.
          </p>
        </div>
        <div
          className="hstack"
          style={{
            padding: "12px 22px",
            background: "var(--panel-2)",
            borderTop: "1px solid var(--line)",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button className="btn" onClick={onDismiss}>
            Dismiss
          </button>
          <button className="btn primary" onClick={onRetry}>
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}
